import { Component, inject } from '@angular/core';
import {
	LO_DIALOG_DATA,
	LoButton,
	LoDialog,
	LoDialogRef,
	LoIcon
} from 'lo-design-system';
import {
	RemoveMemberDialogData,
	RemoveMemberDialogResult
} from './remove-member-dialog.model';

/**
 * What removing a frozen member does, said plainly before it is done.
 *
 * Removal is the one seat action that cannot be pressed back, so the dialog
 * names that first — and then the three things an owner worries about and
 * cannot see from the table: the work stays, the bill does not move (the seat
 * was released when it was frozen), and the door is not closed on the person.
 * Same shape as the freeze dialog beside it.
 */
@Component({
	standalone: true,
	selector: 'app-remove-member-dialog',
	imports: [LoDialog, LoButton, LoIcon],
	template: `
		<lo-dialog heading="Remove {{ data.memberLabel }} from the account?">
			<ul class="consequences" data-testid="remove-consequences">
				@for (line of consequences; track line) {
					<li class="lo-type-text-md">
						<lo-icon name="small_arrow_right" />
						<span>{{ line }}</span>
					</li>
				}
			</ul>

			<p class="lo-type-text-sm note">
				You can invite them again later at the same address. They join
				on a new seat, like anyone else you invite.
			</p>

			<div loDialogActions>
				<button lo-button variant="secondary" type="button" (click)="cancel()">
					Keep them
				</button>
				<button
					lo-button
					variant="danger"
					type="button"
					data-testid="remove-confirm"
					(click)="confirm()"
				>
					Remove for good
				</button>
			</div>
		</lo-dialog>
	`,
	styles: [
		//language=scss
		`
			.consequences {
				display: flex;
				flex-direction: column;
				gap: var(--lo-space-gap-sm);
				margin: 0;
				padding: 0;
				list-style: none;

				li {
					display: flex;
					align-items: flex-start;
					gap: var(--lo-space-gap-sm);

					lo-icon {
						color: var(--lo-color-icon-default);
					}
				}
			}

			.note {
				margin: var(--lo-space-gap-lg) 0 0;
				color: var(--lo-color-text-secondary);
			}
		`
	]
})
export class RemoveMemberDialogComponent {
	readonly consequences: string[] = [
		'They lose access to the account for good. This cannot be undone.',
		'Their work stays in the account — nothing they made is deleted.',
		'Their seat is already free, so your bill does not change.'
	];

	readonly data = inject<RemoveMemberDialogData>(LO_DIALOG_DATA);
	private readonly dialogRef =
		inject<
			LoDialogRef<RemoveMemberDialogComponent, RemoveMemberDialogResult>
		>(LoDialogRef);

	confirm(): void {
		this.dialogRef.close({ confirmed: true });
	}

	cancel(): void {
		this.dialogRef.close({ confirmed: false });
	}
}
